import React from 'react';

type StateToken = string | false | null | undefined;

type PrerenderReadyProps = {
  /** True only once every piece of data the page needs has loaded. */
  ready: boolean;
  /** What the data resolved to, e.g. "enrollable promo" or "no-run". */
  state?: StateToken | StateToken[];
};

/**
 * The signal the build-time crawler snapshot waits for.
 *
 * WHY: crawler documents for the pages that sell are a snapshot of the real
 * React tree (scripts/snapshot-route-documents.js), so the raw HTML Google's
 * ad policy crawler reads carries the same price, dates and guarantee a buyer
 * sees. A snapshot taken while data is still loading would publish a spinner.
 * The page renders this marker only after its required data loaded; the build
 * fails when it never appears. Render nothing on an error state, so a failed
 * price fetch can never be published.
 *
 * `state` names what the data resolved to. src/site/crawlerFacts.js reads it
 * to decide which facts the document must carry: a closed cohort is judged on
 * the facts it can show, never on invented ones.
 */
export default function PrerenderReady({
  ready,
  state,
}: PrerenderReadyProps): React.ReactElement | null {
  if (!ready) return null;
  const tokens = (Array.isArray(state) ? state : [state])
    .filter((token): token is string => typeof token === 'string' && token.length > 0)
    .join(' ');
  return (
    <span
      data-prerender-ready="true"
      data-prerender-state={tokens || undefined}
      hidden
      aria-hidden="true"
    />
  );
}
